import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import NavigationTab from "../navigation";
import { PassReq } from "../passwordreqmgmt/pass_i";

const StyledTableCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  color: "white",
}));

const PasswordReqListTable: React.FC = () => {
  const [passreq, setPassReq] = useState<PassReq[]>([]);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [userToToggle, setUserToToggle] = useState<{ id: number; status: string } | null>(null);

  const fetchPasswordReq = async () => {
    try {
      const token: string | null = localStorage.getItem("token");
      if (!token) return;

      const passResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user/getAll/getAllUserPassReq`,
        {
          method: "GET",
          headers: {
            "x-api-key": token,
            "Content-Type": "application/json",
          },
        }
      );
      const jsonData = await passResponse.json();
      const passArray = Array.isArray(jsonData) ? jsonData : [jsonData];
      setPassReq(passArray);
    } catch (error) {
      console.error("Error fetching password requests:", error);
    }
  };

  useEffect(() => {
    fetchPasswordReq();
  }, []);

  const handleStatusChangeRequest = (id: number, currentStatus: string) => {
    const newStatus = currentStatus === "APPROVED" ? "REJECTED" : "APPROVED";
    setUserToToggle({ id, status: newStatus });
    setStatusDialogOpen(true);
  };

  const handleStatusChangeConfirm = async () => {
    if (!userToToggle) return;
    const token: string | null = localStorage.getItem("token");

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/user/${userToToggle.id}/passwordApproval`,
        {
          method: "PUT",
          headers: {
            "x-api-key": token || "",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ status: userToToggle.status }),
        }
      );

      if (response.ok) {
        setPassReq((prev) => prev.filter((req) => req.id !== userToToggle.id));
      } else {
        console.error("Failed to update request");
      }
    } catch (error) {
      console.error("Error updating request:", error);
    }

    setStatusDialogOpen(false);
  };

  return (
    <Box sx={{ background: "#1a1a1a", paddingBottom: 8 }}>
      <NavigationTab />
      <Grid container>
        <Grid
          item
          xs={12}
          sx={{ display: "flex", justifyContent: "center", alignItems: "center", background: "transparent" }}
        >
          <Box sx={{ width: "1200px", background: "black", border: "1px solid white", borderRadius: "18px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px" }}>
              <Typography variant="h6" sx={{ color: "white" }}>
                Password Request List
              </Typography>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "green" }}>
                    <StyledTableCell>Password</StyledTableCell>
                    <StyledTableCell>Approval Status</StyledTableCell>
                    <StyledTableCell>User Name</StyledTableCell>
                    <StyledTableCell>Date</StyledTableCell>
                    <StyledTableCell>Action</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {passreq.length > 0 ? (
                    passreq.map((request, index) => (
                      <TableRow key={index}>
                        <StyledTableCell>
                          <Typography variant="body1" sx={{ color: "white" }}>
                            {request.password}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography sx={{ color: "white" }}>
                            {request.approved === "APPROVED" ? "Approved" : "Rejected"}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography sx={{ color: "white" }}>{request.user.name}</Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Typography sx={{ color: "white" }}>
                            {new Date(Date.now() - index * 86400000).toLocaleDateString()}
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell>
                          <IconButton
                            sx={{ backgroundColor: "green", marginRight: "5px" }}
                            onClick={() => handleStatusChangeRequest(request.id, request.approved)}
                          >
                            <CheckIcon sx={{ color: "white" }} />
                          </IconButton>
                          <IconButton
                            sx={{ backgroundColor: "red", marginLeft: "5px" }}
                            onClick={() => handleStatusChangeRequest(request.id, request.approved)}
                          >
                            <CloseIcon sx={{ color: "white" }} />
                          </IconButton>
                        </StyledTableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography sx={{ color: "white", fontWeight: "bold" }}>No pending requests</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Grid>
      </Grid>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {userToToggle?.status === "APPROVED" ? "approve" : "reject"} this request?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleStatusChangeConfirm} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PasswordReqListTable;
